import React, { useEffect, useMemo, useState } from "react";
import useGetRequest from "../../hooks/useGetRequest";
import { ISettings } from "../../types/settings.type";
import { api } from "../../api/api";
import {
  Alert,
  Button,
  ConfigProvider,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Spin,
  Switch,
} from "antd";
import {
  FiPower,
  FiSliders,
  FiClipboard,
  FiTrendingUp,
  FiPercent,
  FiFileText,
} from "react-icons/fi";
import { useAuth } from "../../contexts/auth.context";
import { hasAdminAccess } from "../../utils/roleUtils";
import SalesPriceLimitSettings from "../../components/SalesPriceLimitSettings/salesPriceLimitSettings.component";
import "./globalSettings.page.css";

interface SectionDef {
  key: string;
  label: string;
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  description: string;
  adminOnly?: boolean;
  fieldNames: string[];
}

const SECTIONS: SectionDef[] = [
  {
    key: "system",
    label: "Система",
    icon: <FiPower />,
    eyebrow: "Раздел 1",
    title: "Система",
    description: "Аварийный выключатель доступа. Держите этот раздел под рукой на случай техработ.",
    adminOnly: true,
    fieldNames: [],
  },
  {
    key: "salesLimit",
    label: "Лимит продаж",
    icon: <FiSliders />,
    eyebrow: "Раздел 2",
    title: "Лимит продаж",
    description: "Ограничение минимальной цены продажи для продажников — можно временно отключить.",
    adminOnly: true,
    fieldNames: [],
  },
  {
    key: "yuk",
    label: "Юк / Филлер",
    icon: <FiClipboard />,
    eyebrow: "Раздел 3",
    title: "Юк / Филлер",
    description: "Цены и лимиты, по которым работает филлер при учёте юков.",
    fieldNames: ["yukPriceUSD", "yukPriceUZS", "yukLimit", "minuteLimit", "maxFillerName"],
  },
  {
    key: "exchangeRate",
    label: "Курс валют",
    icon: <FiTrendingUp />,
    eyebrow: "Раздел 4",
    title: "Курс валют",
    description: "Границы, в которых система принимает установленный курс USD/UZS.",
    fieldNames: ["exchangeRateLowLimit", "exchangeRateUpLimit"],
  },
  {
    key: "discounts",
    label: "Скидки",
    icon: <FiPercent />,
    eyebrow: "Раздел 5",
    title: "Скидки и оплата",
    description: "Правила скидок, применяемые автоматически при оформлении продаж.",
    fieldNames: ["paidImmediatelyDiscountPercent", "taskOrderDiscountLimit"],
  },
  {
    key: "proposal",
    label: "КП",
    icon: <FiFileText />,
    eyebrow: "Раздел 6",
    title: "Коммерческие предложения",
    description: "Значения по умолчанию для расчёта срока и оформления документа КП.",
    fieldNames: [
      "proposalValidityDays",
      "defaultDailyCapacity",
      "proposalLeadTimeSafetyPercent",
      "proposalTemplateVariant",
      "proposalDirectorName",
      "proposalDirectorPhone",
      "proposalSalesDirectorName",
      "proposalSalesDirectorPhone",
      "proposalNotes",
      "proposalDiscountLimit",
      "proposalAdminDiscountLimit",
    ],
  },
  {
    key: "smartPrice",
    label: "Умный прайс",
    icon: <FiPercent />,
    eyebrow: "Раздел 7",
    title: "Умный прайс",
    description:
      "Дефолты, если у категории продукции свои значения не заданы: мин. % прибыли даёт мин. цену (пол продажи), макс. скидка ограничивает уступку от прайса. Прайс умный прайс не трогает.",
    fieldNames: ["defaultMinProfitPercent", "defaultMaxDiscountPercent"],
  },
  {
    key: "kgLimits",
    label: "Лимиты кг",
    icon: <FiSliders />,
    eyebrow: "Раздел 8",
    title: "Лимиты количества-2 (кг)",
    description:
      "Сверка фактических кг с нормой группы продукции. Можно отключить отдельно для производства и для заказа продажи.",
    adminOnly: true,
    fieldNames: [
      "productionKgLimit",
      "saleKgLimit",
      "kgLimitExemptUserId",
      "materialPurchasePriceLimit",
    ],
  },
  {
    key: "processingLimits",
    label: "Переработка",
    icon: <FiSliders />,
    eyebrow: "Раздел 9",
    title: "Лимиты переработки",
    description:
      "Потолок потери (угара) и коридоры выхода при переработке. Нормы и допуски каждого потока задаются в карточке линии — здесь только общая планка.",
    adminOnly: true,
    fieldNames: [
      "processingLossLimit",
      "processingLossLimitPercent",
      "processingOutputLimit",
    ],
  },
];

function FieldRow({
  label,
  help,
  children,
}: {
  label: string;
  help: string;
  children: React.ReactNode;
}) {
  return (
    <div className="gs-field-row">
      <div>
        <p className="gs-field-label">{label}</p>
        <p className="gs-field-help">{help}</p>
      </div>
      <div className="gs-field-control">{children}</div>
    </div>
  );
}

export default function GlobalSettings() {
  const { data, loading, error, refresh } = useGetRequest("/settings");
  const { data: maintenanceData, refresh: refreshMaintenance } = useGetRequest("/settings/maintenance-mode");
  const { authState } = useAuth();
  const [settings, setSettings] = useState<ISettings | null>(null);
  const [maintenanceMode, setMaintenanceMode] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isMaintenanceLoading, setIsMaintenanceLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isAdmin = hasAdminAccess(authState.user?.role);

  const visibleSections = useMemo(
    () => SECTIONS.filter((section) => !section.adminOnly || isAdmin),
    [isAdmin]
  );
  const [activeSection, setActiveSection] = useState<string>(visibleSections[0]?.key ?? "yuk");

  const [form] = Form.useForm();
  // Завсклады для выбора того, кому лимит кг только предупреждает.
  const [warehouseMgrs, setWarehouseMgrs] = useState<Array<{ _id: string; name: string }>>([]);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/user")
      .then((response) => {
        const users = response.data?.users ?? [];
        setWarehouseMgrs(
          users.filter((user: { role?: string }) => user.role === "warehouseMgr")
        );
      })
      .catch(() => setWarehouseMgrs([]));
  }, [isAdmin]);

  useEffect(() => {
    if (data?.settings) {
      setSettings(data.settings);
    }
  }, [data]);

  useEffect(() => {
    if (maintenanceData?.maintenanceMode !== undefined) {
      setMaintenanceMode(maintenanceData.maintenanceMode);
    }
  }, [maintenanceData]);

  useEffect(() => {
    form.setFieldsValue({
      ...settings,
      proposalValidityDays: settings?.proposalValidityDays ?? 30,
      proposalTemplateVariant: settings?.proposalTemplateVariant ?? "corporate",
      productionKgLimit: settings?.productionKgLimit ?? true,
      saleKgLimit: settings?.saleKgLimit ?? true,
      kgLimitExemptUserId: settings?.kgLimitExemptUserId ?? null,
      proposalDiscountLimit: settings?.proposalDiscountLimit ?? true,
      proposalAdminDiscountLimit: settings?.proposalAdminDiscountLimit ?? false,
      taskOrderDiscountLimit: settings?.taskOrderDiscountLimit ?? true,
      processingLossLimit: settings?.processingLossLimit ?? true,
      processingLossLimitPercent: settings?.processingLossLimitPercent ?? 2,
      processingOutputLimit: settings?.processingOutputLimit ?? true,
      materialPurchasePriceLimit: settings?.materialPurchasePriceLimit ?? true,
    });
    setIsDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const updateSettings = async (values: ISettings) => {
    setIsSaving(true);
    try {
      await api.post("/settings", values);
      message.success("Настройки сохранены");
      refresh();
    } catch (error) {
      message.error("Ошибка при сохранении настроек.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinish = (values: any) => {
    // Очистка селекта даёт undefined, а бэкенд считает undefined «поле не
    // прислали» и оставляет прежнего завсклада. Явный null снимает послабление.
    updateSettings({ ...values, kgLimitExemptUserId: values.kgLimitExemptUserId ?? null });
  };

  const handleDiscard = () => {
    form.setFieldsValue({
      ...settings,
      proposalValidityDays: settings?.proposalValidityDays ?? 30,
      proposalTemplateVariant: settings?.proposalTemplateVariant ?? "corporate",
      productionKgLimit: settings?.productionKgLimit ?? true,
      saleKgLimit: settings?.saleKgLimit ?? true,
      kgLimitExemptUserId: settings?.kgLimitExemptUserId ?? null,
      proposalDiscountLimit: settings?.proposalDiscountLimit ?? true,
      proposalAdminDiscountLimit: settings?.proposalAdminDiscountLimit ?? false,
      taskOrderDiscountLimit: settings?.taskOrderDiscountLimit ?? true,
      processingLossLimit: settings?.processingLossLimit ?? true,
      processingLossLimitPercent: settings?.processingLossLimitPercent ?? 2,
      processingOutputLimit: settings?.processingOutputLimit ?? true,
      materialPurchasePriceLimit: settings?.materialPurchasePriceLimit ?? true,
    });
    setIsDirty(false);
  };

  const toggleMaintenanceMode = async (checked: boolean) => {
    setIsMaintenanceLoading(true);
    try {
      await api.post("/settings/maintenance-mode", {
        maintenanceMode: checked,
      });
      setMaintenanceMode(checked);
      message.success(
        checked
          ? "Режим обслуживания включен. Все пользователи, кроме админов, будут заблокированы."
          : "Режим обслуживания выключен. Доступ восстановлен для всех пользователей."
      );
      refreshMaintenance();
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при изменении режима обслуживания");
    } finally {
      setIsMaintenanceLoading(false);
    }
  };

  if (error) {
    return <Alert message="Ошибка при загрузке данных" type="error" showIcon />;
  }

  if (loading) {
    return <Spin tip="Загрузка..." />;
  }

  const activeMeta = SECTIONS.find((section) => section.key === activeSection) ?? SECTIONS[2];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#e08a1e",
          borderRadius: 4,
          fontFamily:
            'ui-monospace, "SF Mono", "Cascadia Mono", "Roboto Mono", Menlo, Consolas, monospace',
        },
      }}
    >
      <div className="gs-root">
        <div className="gs-header">
          <div>
            <h1 className="gs-title">Настройки</h1>
            <p className="gs-subtitle">
              Параметры, влияющие на работу всей платформы: цены и лимиты филлера, границы курса валют,
              скидки и генерация коммерческих предложений.
            </p>
          </div>
        </div>

        <div className="gs-body">
          <nav className="gs-nav">
            {visibleSections.map((section) => (
              <button
                key={section.key}
                type="button"
                className={`gs-nav-item${activeSection === section.key ? " active" : ""}`}
                onClick={() => setActiveSection(section.key)}
              >
                <span className="gs-nav-icon">{section.icon}</span>
                {section.label}
                {section.fieldNames.length > 0 &&
                  form.isFieldsTouched(section.fieldNames) && <span className="gs-nav-dot" />}
              </button>
            ))}
          </nav>

          <div className="gs-content">
            {/* Раздел "Система" и "Лимит продаж" не входят в общую форму настроек —
                у них собственные API и применяются мгновенно, без кнопки "Сохранить". */}
            {activeSection === "system" && isAdmin && (
              <div>
                <p className="gs-section-eyebrow">{activeMeta.eyebrow}</p>
                <h2 className="gs-section-title">{activeMeta.title}</h2>
                <p className="gs-section-desc">{activeMeta.description}</p>

                <div className="gs-status-card">
                  <div>
                    <p className="gs-status-label">Режим обслуживания</p>
                    <p className={`gs-status-value ${maintenanceMode ? "danger" : "ok"}`}>
                      {maintenanceMode
                        ? "Активен — доступ есть только у админов"
                        : "Выключен — доступ есть у всех пользователей"}
                    </p>
                  </div>
                  <Switch
                    checked={maintenanceMode}
                    onChange={toggleMaintenanceMode}
                    loading={isMaintenanceLoading}
                    checkedChildren="Вкл"
                    unCheckedChildren="Выкл"
                  />
                </div>
                <p className="gs-field-help">
                  При включении все запросы от пользователей без роли админа блокируются с сообщением о
                  технических работах. Применяется мгновенно, отдельного сохранения не требует.
                </p>
              </div>
            )}

            {activeSection === "salesLimit" && isAdmin && (
              <div>
                <p className="gs-section-eyebrow">{activeMeta.eyebrow}</p>
                <h2 className="gs-section-title">{activeMeta.title}</h2>
                <p className="gs-section-desc">{activeMeta.description}</p>
                <SalesPriceLimitSettings />
              </div>
            )}

            <Form
              form={form}
              onFinish={handleFinish}
              onValuesChange={() => setIsDirty(form.isFieldsTouched())}
              layout="vertical"
              style={{ display: activeSection === "system" || activeSection === "salesLimit" ? "none" : "block" }}
            >
              <div style={{ display: activeSection === "yuk" ? "block" : "none" }}>
                <p className="gs-section-eyebrow">{SECTIONS[2].eyebrow}</p>
                <h2 className="gs-section-title">{SECTIONS[2].title}</h2>
                <p className="gs-section-desc">{SECTIONS[2].description}</p>

                <FieldRow label="Цена юка, USD" help="Стоимость единицы юка в долларах — база для расчёта себестоимости филлера.">
                  <Form.Item name="yukPriceUSD" rules={[{ required: true, message: "Введите цену юка" }]}>
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                </FieldRow>
                <FieldRow label="Цена юка, UZS" help="То же значение в сумах — для расчётов в локальной валюте.">
                  <Form.Item name="yukPriceUZS" rules={[{ required: true, message: "Введите цену юка" }]}>
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                </FieldRow>
                <FieldRow label="Юк лимит" help="Максимальное количество юков на одну операцию филлера.">
                  <Form.Item name="yukLimit" rules={[{ required: true, message: "Введите юк лимит" }]}>
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                </FieldRow>
                <FieldRow label="Лимит минут" help="Сколько минут даётся филлеру на операцию до истечения лимита.">
                  <Form.Item name="minuteLimit" rules={[{ required: true, message: "Введите минутный лимит" }]}>
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                </FieldRow>
                <FieldRow label="Макс имя филлера" help="Максимальная длина имени филлера, допустимая при вводе.">
                  <Form.Item name="maxFillerName" rules={[{ required: true, message: "Введите макс. имя филлера" }]}>
                    <Input />
                  </Form.Item>
                </FieldRow>
              </div>

              <div style={{ display: activeSection === "exchangeRate" ? "block" : "none" }}>
                <p className="gs-section-eyebrow">{SECTIONS[3].eyebrow}</p>
                <h2 className="gs-section-title">{SECTIONS[3].title}</h2>
                <p className="gs-section-desc">{SECTIONS[3].description}</p>

                <FieldRow label="Нижний лимит курса" help="Ниже этого значения система не примет установленный курс валюты.">
                  <Form.Item name="exchangeRateLowLimit" rules={[{ required: true, message: "Введите нижний лимит курса" }]}>
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                </FieldRow>
                <FieldRow label="Верхний лимит курса" help="Выше этого значения система не примет установленный курс валюты.">
                  <Form.Item name="exchangeRateUpLimit" rules={[{ required: true, message: "Введите верхний лимит курса" }]}>
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                </FieldRow>
              </div>

              <div style={{ display: activeSection === "discounts" ? "block" : "none" }}>
                <p className="gs-section-eyebrow">{SECTIONS[4].eyebrow}</p>
                <h2 className="gs-section-title">{SECTIONS[4].title}</h2>
                <p className="gs-section-desc">{SECTIONS[4].description}</p>

                <FieldRow
                  label="Скидка за оплату сразу"
                  help="Скидка от минимальной цены, если клиент платит сразу, а не в рассрочку/долг. По умолчанию 5%."
                >
                  <Form.Item
                    name="paidImmediatelyDiscountPercent"
                    rules={[{ type: "number", min: 0, max: 100, message: "Процент должен быть от 0 до 100" }]}
                  >
                    <InputNumber min={0} max={100} style={{ width: "100%" }} addonAfter="%" />
                  </Form.Item>
                </FieldRow>

                <FieldRow
                  label="Лимит скидки в заказ-наряде"
                  help="Главный рубильник цены в заказ-наряде. Включён: цена не может опускаться ниже «прайс минус макс. скидка категории» (та же цифра, что в КП и в колонке «Макс. скидка (цена)» на «Прайсах») и ниже минимальной цены продаж — на всех ролях, включая админа. Выключен: цена не проверяется вообще, «Лимит продаж» (Раздел 2) в заказ-наряде тоже не действует."
                >
                  <Form.Item name="taskOrderDiscountLimit" valuePropName="checked" noStyle>
                    <Switch checkedChildren="Вкл" unCheckedChildren="Выкл" />
                  </Form.Item>
                </FieldRow>
              </div>

              <div style={{ display: activeSection === "proposal" ? "block" : "none" }}>
                <p className="gs-section-eyebrow">{SECTIONS[5].eyebrow}</p>
                <h2 className="gs-section-title">{SECTIONS[5].title}</h2>
                <p className="gs-section-desc">{SECTIONS[5].description}</p>

                <FieldRow
                  label="Срок действия КП"
                  help="Через сколько дней после создания коммерческое предложение считается просроченным."
                >
                  <Form.Item
                    name="proposalValidityDays"
                    rules={[{ type: "integer", min: 1, message: "Введите положительное целое число" }]}
                  >
                    <InputNumber min={1} precision={0} style={{ width: "100%" }} addonAfter="дн." />
                  </Form.Item>
                </FieldRow>
                <FieldRow
                  label="Производительность по умолчанию"
                  help="Ед. продукции в день — используется в расчёте срока КП, если у самой продукции своя производительность не задана."
                >
                  <Form.Item name="defaultDailyCapacity">
                    <InputNumber min={1} style={{ width: "100%" }} placeholder="Напр. 1000" addonAfter="ед./день" />
                  </Form.Item>
                </FieldRow>
                <FieldRow
                  label="Страховка к сроку выполнения"
                  help="Запас времени поверх расчётного срока на случай задержек. Напр. 50% → 10 дней превращаются в 15."
                >
                  <Form.Item
                    name="proposalLeadTimeSafetyPercent"
                    rules={[{ type: "number", min: 0, message: "Не может быть отрицательным" }]}
                  >
                    <InputNumber min={0} style={{ width: "100%" }} placeholder="Напр. 50" addonAfter="%" />
                  </Form.Item>
                </FieldRow>
                <FieldRow label="Шаблон КП" help="Визуальное оформление документа КП при предпросмотре и экспорте в PDF.">
                  <Form.Item name="proposalTemplateVariant">
                    <Select
                      options={[
                        { value: "corporate", label: "Корпоративный" },
                        { value: "premium", label: "Премиум" },
                        { value: "modern", label: "Модерн" },
                      ]}
                    />
                  </Form.Item>
                </FieldRow>
                <FieldRow
                  label="ФИО директора"
                  help="Печатается в реквизитах документа КП: «Директор: Ф. И. О., тел. …». Оставьте пустым, чтобы не показывать."
                >
                  <Form.Item name="proposalDirectorName">
                    <Input placeholder="Например: Маликов Абдулла Абдуллаевич" />
                  </Form.Item>
                </FieldRow>
                <FieldRow
                  label="Телефон директора"
                  help="Показывается рядом с ФИО в реквизитах и в футере каждой страницы КП. Для уже созданных КП не меняется."
                >
                  <Form.Item name="proposalDirectorPhone">
                    <Input placeholder="Например: +998 91 234-56-78" />
                  </Form.Item>
                </FieldRow>
                <FieldRow
                  label="ФИО директора по продажам"
                  help="Печатается в реквизитах документа КП вместе с директором: «Директор по продажам: Ф. И. О., тел. …». Оставьте пустым, чтобы не показывать."
                >
                  <Form.Item name="proposalSalesDirectorName">
                    <Input placeholder="Например: Каримов Шерзод Бахтиярович" />
                  </Form.Item>
                </FieldRow>
                <FieldRow
                  label="Телефон директора по продажам"
                  help="Показывается рядом с ФИО в реквизитах и в футере каждой страницы КП. Для уже созданных КП не меняется."
                >
                  <Form.Item name="proposalSalesDirectorPhone">
                    <Input placeholder="Например: +998 91 234-56-78" />
                  </Form.Item>
                </FieldRow>
                <FieldRow
                  label="Лимит скидки в КП"
                  help="Скидка не может опускать цену ниже минимальной и превышать макс. скидку клиента. Выключите, чтобы разрешить любую скидку в КП."
                >
                  <Form.Item name="proposalDiscountLimit" valuePropName="checked" noStyle>
                    <Switch checkedChildren="Вкл" unCheckedChildren="Выкл" />
                  </Form.Item>
                </FieldRow>
                <Form.Item noStyle shouldUpdate={(prev, next) => prev.proposalDiscountLimit !== next.proposalDiscountLimit}>
                  {({ getFieldValue }) => (
                    <FieldRow
                      label="Лимит скидки для админов"
                      help={
                        getFieldValue("proposalDiscountLimit") === false
                          ? "Общий лимит скидки выключен — скидки не ограничены ни для кого."
                          : "Распространить лимит скидки на админа, директора и руководителя. Выключено — эти роли делают скидки без ограничений."
                      }
                    >
                      <Form.Item name="proposalAdminDiscountLimit" valuePropName="checked" noStyle>
                        <Switch
                          checkedChildren="Вкл"
                          unCheckedChildren="Выкл"
                          disabled={getFieldValue("proposalDiscountLimit") === false}
                        />
                      </Form.Item>
                    </FieldRow>
                  )}
                </Form.Item>
                <FieldRow
                  label="Примечания в КП"
                  help="Дополнительные пункты со звёздочкой (*) в конце документа. Для уже созданных КП не меняются."
                >
                  <Form.List name="proposalNotes">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field) => (
                          <div key={field.key} className="gs-notes-row">
                            <Form.Item
                              name={field.name}
                              noStyle
                              rules={[{ required: true, message: "Введите текст примечания" }]}
                            >
                              <Input placeholder="Например: Цена указана с учётом доставки." />
                            </Form.Item>
                            <Button danger onClick={() => remove(field.name)}>
                              Удалить
                            </Button>
                          </div>
                        ))}
                        <Button type="dashed" block onClick={() => add("")}>
                          + Добавить примечание
                        </Button>
                      </>
                    )}
                  </Form.List>
                </FieldRow>
              </div>

              <div style={{ display: activeSection === "smartPrice" ? "block" : "none" }}>
                <p className="gs-section-eyebrow">{SECTIONS[6].eyebrow}</p>
                <h2 className="gs-section-title">{SECTIONS[6].title}</h2>
                <p className="gs-section-desc">{SECTIONS[6].description}</p>

                <FieldRow
                  label="Мин. % прибыли (себестоимость → мин. цена)"
                  help="Наценка на себестоимость для минимальной цены: мин = себестоимость × (1 + %). Это пол продажи. Применяется, если у категории продукции свой процент не задан."
                >
                  <Form.Item
                    name="defaultMinProfitPercent"
                    rules={[{ type: "number", min: 0, message: "Не может быть отрицательным" }]}
                  >
                    <InputNumber min={0} style={{ width: "100%" }} placeholder="Напр. 20" addonAfter="%" />
                  </Form.Item>
                </FieldRow>

                <FieldRow
                  label="Макс. скидка от прайса, %"
                  help="Потолок уступки продажника в КП и заказах СРМ. Цена после скидки в любом случае не опускается ниже мин. цены. Применяется, если у категории продукции свой процент не задан."
                >
                  <Form.Item
                    name="defaultMaxDiscountPercent"
                    rules={[{ type: "number", min: 0, max: 100, message: "Допустимо от 0 до 100%" }]}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      style={{ width: "100%" }}
                      placeholder="Напр. 15"
                      addonAfter="%"
                    />
                  </Form.Item>
                </FieldRow>
              </div>

              {isAdmin && (
                <div style={{ display: activeSection === "kgLimits" ? "block" : "none" }}>
                  <p className="gs-section-eyebrow">{SECTIONS[7].eyebrow}</p>
                  <h2 className="gs-section-title">{SECTIONS[7].title}</h2>
                  <p className="gs-section-desc">{SECTIONS[7].description}</p>

                  <FieldRow
                    label="Лимит кг в производстве"
                    help="При создании отчёта производства фактические кг должны попасть в коридор нормы группы. Выключите, чтобы не проверять кг при производстве."
                  >
                    <Form.Item name="productionKgLimit" valuePropName="checked" noStyle>
                      <Switch checkedChildren="Вкл" unCheckedChildren="Выкл" />
                    </Form.Item>
                  </FieldRow>
                  <FieldRow
                    label="Лимит кг в заказе продажи"
                    help="При создании заказа продажи (складская продажа) фактические кг должны попасть в коридор нормы группы. Выключите, чтобы не проверять кг при продаже. Админа лимит не блокирует никогда — он получает предупреждение, а заказ сохраняется с пометкой."
                  >
                    <Form.Item name="saleKgLimit" valuePropName="checked" noStyle>
                      <Switch checkedChildren="Вкл" unCheckedChildren="Выкл" />
                    </Form.Item>
                  </FieldRow>
                  <FieldRow
                    label="Завсклад без лимита"
                    help="Этому завскладу лимит кг в производстве не запрещает сдать отчёт: он увидит предупреждение, а строка сохранится с пометкой «Выше лимита» или «Ниже лимита». Такой аккаунт может быть только один."
                  >
                    <Form.Item name="kgLimitExemptUserId" noStyle>
                      <Select
                        allowClear
                        placeholder="Не выбран — лимит блокирует всех"
                        style={{ minWidth: 260 }}
                        options={warehouseMgrs.map((user) => ({
                          value: user._id,
                          label: user.name,
                        }))}
                      />
                    </Form.Item>
                  </FieldRow>
                  <FieldRow
                    label="Лимит закупочной цены материала"
                    help="Запрещает купить материал дороже его «Цены до». Действует только там, где «Цена до» задана в валюте заказа. Выключите, чтобы закупка проходила — предупреждение о переплате всё равно сохранится в заказе."
                  >
                    <Form.Item name="materialPurchasePriceLimit" valuePropName="checked" noStyle>
                      <Switch checkedChildren="Вкл" unCheckedChildren="Выкл" />
                    </Form.Item>
                  </FieldRow>
                </div>
              )}

              {isAdmin && (
                <div style={{ display: activeSection === "processingLimits" ? "block" : "none" }}>
                  <p className="gs-section-eyebrow">{SECTIONS[8].eyebrow}</p>
                  <h2 className="gs-section-title">{SECTIONS[8].title}</h2>
                  <p className="gs-section-desc">{SECTIONS[8].description}</p>

                  <FieldRow
                    label="Лимит потери при переработке"
                    help="Загрузка не проводится, если потеря (угар) вышла за потолок ниже. Выключите, чтобы вообще не проверять потерю — коридоры выходов при этом продолжат работать, у них свой тумблер."
                  >
                    <Form.Item name="processingLossLimit" valuePropName="checked" noStyle>
                      <Switch checkedChildren="Вкл" unCheckedChildren="Выкл" />
                    </Form.Item>
                  </FieldRow>

                  <FieldRow
                    label="Потолок потери"
                    help="Максимальная доля угара от загруженного веса. Внимание: 0 означает «стоп при любой потере», а не «лимит выключен» — чтобы снять проверку, выключите тумблер выше. Завскладу без лимита потолок только предупреждает."
                  >
                    <Form.Item name="processingLossLimitPercent" noStyle>
                      <InputNumber min={0} max={100} step={0.1} addonAfter="%" style={{ minWidth: 160 }} />
                    </Form.Item>
                  </FieldRow>

                  <FieldRow
                    label="Лимит коридоров выхода"
                    help="Загрузка не проводится, если какой-то выход вышел за свой коридор нормы. Нормы и допуски задаются отдельно для каждого потока в карточке линии переработки. На время притирки нового аппарата этот тумблер удобно выключить: отклонения будут записываться в отчёты, но работу не остановят."
                  >
                    <Form.Item name="processingOutputLimit" valuePropName="checked" noStyle>
                      <Switch checkedChildren="Вкл" unCheckedChildren="Выкл" />
                    </Form.Item>
                  </FieldRow>
                </div>
              )}
            </Form>
          </div>
        </div>

        {isDirty && (
          <div className="gs-savebar">
            <div className="gs-savebar-status">
              <span className="gs-savebar-dot" />
              Есть несохранённые изменения
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={handleDiscard} disabled={isSaving}>
                Отменить
              </Button>
              <Button type="primary" loading={isSaving} onClick={() => form.submit()}>
                Сохранить
              </Button>
            </div>
          </div>
        )}
      </div>
    </ConfigProvider>
  );
}
